import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ThemeProvider } from 'styled-components';
import ReactModal from 'react-modal';
import { enableMapSet, enablePatches } from 'immer';
import * as Sentry from '@sentry/nextjs';

import '../constants/locales';
import 'leaflet/dist/leaflet.css';
import 'nprogress/nprogress.css';
import '../components/timeline/react-calendar-timeline/lib/Timeline.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';

import '../components/reporting/react-select.css';
import '../components/theme/normalize.css';
import './admin/index.css';
import '../components/notifications/notifications.css';
import '../components/global.css';

import appTheme from '../components/theme';
import GlobalStyle from '../components/theme/globalStyles';
import { removeToken, useParsedToken } from '../lib/cookieParsers';
import { useCurrentUserQuery } from '../graphql';
import { ApolloProvider } from '@apollo/client';
import FeatureContext from '../components/contexts/FeatureContext';
import type { GetServerSidePropsContext } from 'next';
import type { AppProps } from 'next/app';
import type { CustomSentryError } from './_error';
import { LoadingOverlay } from '../components/LoadingSpinner';
import initApollo from '../lib/init-apollo';
import { TimelinePreferencesProvider } from '../components/contexts/TimelinePreferencesContext';
import { DistanceProvider } from '../components/distances/DistanceProvider';
import { timeTravel } from '../lib/timekeeper';
import localForage from 'localforage';
import { setPlanningAssistantWorkerInitializer } from '../components/core/PlanningAssistantWorkerMaster';
import type { User } from '../state/zustand';
import { userFromQueryData } from '../state/zustand/currentUser';
import { setInitialValues } from '../state/zustand';
import { useFetchBunkerExPrices } from '../components/hooks/useBunkerPrices';
import { useCurrencyApi } from '../components/hooks/useCurrencyApi';
import ToastNotifications from '../components/notifications/ToastNotifications';
import VersionCheck from '../components/notifications/VersionCheck';
import { useSetAtom } from 'jotai';
import { apolloMutationCompletedAtAtom } from '../components/Scenario';
import { useGlobalAllLocationsQuery } from '../lib/allLocations';
import GrooveChatWidget from '../components/GrooveChatWidget';
import Navigation from '../components/Navigation';
import SignIn from './signin';
import type { ComputedScenarioTimings } from '../lib/planningData';
import { useComputedScenarioTimings } from '../lib/planningData';
import { DialogsProvider } from '../components/ui/Modal/useDialog';
import { datadogRum } from '@datadog/browser-rum';
import DatadogInit from '../lib/datadog/datadog-init';
import ForgotPassword from './forgot-password';
import SetPassword from './set-password';
import WebSocketStatusNotifier from '../components/notifications/WebSocketStatus';
import { MarcuraPortCostProvider } from '../components/marcura/MarcuraPortCostProvider';
import { NotificationsProvider } from '../components/contexts/NotificationsContext';

const googleFont =
  typeof document !== 'undefined' && document.getElementById('google_font');
if (googleFont) {
  googleFont.removeAttribute('media');
}

localForage.config();
enableMapSet();
enablePatches();

ReactModal.setAppElement('#__next');

const isBrowser = typeof window !== 'undefined';

if (isBrowser) {
  // Initialize background worker for PlanningAssistant
  // We provide it as an initializer in order to only load the bundle in case the feature is used
  // Also note it's initialized here instead of Core so that we don't create a circular dependency
  setPlanningAssistantWorkerInitializer(() => {
    return Promise.resolve(
      new Worker(
        new URL(
          '../components/core/PlanningAssistantWorker.ts',
          import.meta.url,
        ),
      ),
    );
  });

  if (process.env.APP_ENV === 'development' || process.env.APP_ENV === 'e2e') {
    void import('../lib/dev-tools');
  }
}

if (
  process.env.APP_ENV === 'development' &&
  process.env.TIME_TRAVEL &&
  isBrowser
) {
  const bestMomentForUsingSeedData = new Date('2022-07-20T07:00:00.000Z');
  timeTravel(bestMomentForUsingSeedData);
}

export interface MainAppProps extends AppProps {
  err?: CustomSentryError;
  Component: AppProps['Component'] & {
    publicPage?: boolean;
    getLayout?: (page: React.ReactElement) => React.ReactNode;
  };
  ctx?: GetServerSidePropsContext; // Only present when page is rendered on server side
}

const useLogin = () => {
  const [userDataLoading, setUserDataLoading] = useState<boolean>(true);
  const jwtToken = useParsedToken();
  const { data, loading: queryLoading } = useCurrentUserQuery({
    skip: jwtToken === null,
  });

  const currentUser = useMemo<User | undefined>(
    () => userFromQueryData(data, jwtToken),
    [data, jwtToken],
  );

  // set user data in Sentry
  if (currentUser) {
    const userId = `${currentUser.id}`;
    const userName = currentUser.name ?? '';
    const userEmail = currentUser.email ?? '';
    const organizationId = `${currentUser.organizationId}`;
    Sentry.setUser({
      userid: userId,
      username: userName,
      id: userEmail,
      email: userEmail,
      org_id: organizationId,
      ip_address: '{{auto}}',
    });
    Sentry.setTag('organization_id', organizationId);
  } else {
    Sentry.setUser(null);
  }

  // set user data in Datadog
  if (currentUser) {
    datadogRum.setUser({
      id: `${currentUser.id}`,
      name: currentUser.name ?? '',
      email: currentUser.email ?? '',
      org_id: `${currentUser.organizationId}`,
    });
  }

  useEffect(() => {
    const isLoading =
      queryLoading || Boolean(jwtToken && !currentUser && !data);
    setUserDataLoading(isLoading);

    if (currentUser) {
      setInitialValues(currentUser);
    } else if (!queryLoading && jwtToken) {
      removeToken();
    }
  }, [currentUser, queryLoading, jwtToken, data]);

  return { currentUser, userDataLoading, jwtToken };
};

export type GlobalPageProps = {
  scenarioTimings: ComputedScenarioTimings;
};
const WithScenarioTimings = (props: {
  currentUser: User | undefined;
  Component: MainAppProps['Component'];
  pageProps: MainAppProps['pageProps'];
  pathname: string | false;
  err: MainAppProps['err'];
}) => {
  const { pathname, currentUser, Component, pageProps, err } = props;
  const isOperations = pathname === '/operations';
  const scenarioTimings = useComputedScenarioTimings({
    ignoreShipownerTimelines: !isOperations,
    isShipOwner: currentUser?.organization?.shipOwner,
    skipLocalScenarios: isOperations,
    autoUpdateUserScenarios:
      !!currentUser?.rootOrganization?.configAutoSyncUserScenarios,
  });
  return (
    <Component {...pageProps} scenarioTimings={scenarioTimings} err={err} />
  );
};

const MainApp = (props: MainAppProps) => {
  const { Component, err, pageProps, ctx } = props;
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout || ((page) => page);

  const router = useRouter();
  const { currentUser, userDataLoading, jwtToken } = useLogin();

  useGlobalAllLocationsQuery(currentUser);
  const currencyRatesReady = useCurrencyApi(currentUser);
  const bunkerExPricesReady = useFetchBunkerExPrices(currentUser);

  if (
    jwtToken &&
    (userDataLoading || !currencyRatesReady || !bunkerExPricesReady)
  ) {
    return <LoadingOverlay />;
  }

  const pathname = router.pathname || (isBrowser && location.pathname);
  if (!Component.publicPage && !currentUser) {
    if (pathname !== '/signin' && pathname !== '/_error') {
      if (isBrowser) {
        // Using this instead of `Router.push('/signin')` to enforce page reload so users have the latest version
        window.location.href = '/signin';
      } else {
        ctx?.res?.writeHead(302, { Location: '/signin' }).end();
      }
    }
    return <LoadingOverlay />;
  }

  // Simple url based feature flags
  const { query } = router;

  const queryFeatures =
    typeof query.features === 'string' ? query.features : '';

  const storeAvailable =
    typeof localStorage !== 'undefined' &&
    typeof localStorage.getItem === 'function' &&
    typeof localStorage.setItem === 'function';

  const storedFeatures = storeAvailable
    ? localStorage.getItem('features') || ''
    : '';

  const features =
    'features' in query
      ? queryFeatures.length > 0
        ? queryFeatures.split(',').filter((f) => f.length > 0)
        : []
      : storedFeatures.split(',').filter((f) => f.length > 0);

  if (storeAvailable && 'features' in query) {
    localStorage.setItem('features', features.join(','));
  }

  return (
    <>
      <DialogsProvider>
        <NotificationsProvider>
          <ToastNotifications />
          <Navigation />
          <FeatureContext.Provider value={features}>
            <DistanceProvider>
              <MarcuraPortCostProvider>
                <TimelinePreferencesProvider>
                  {getLayout(
                    <WithScenarioTimings
                      currentUser={currentUser}
                      Component={Component}
                      pageProps={pageProps}
                      pathname={pathname}
                      err={err}
                    />,
                  )}
                  <div
                    id="dropdown-root"
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      zIndex: 2000,
                    }}
                  />
                </TimelinePreferencesProvider>
              </MarcuraPortCostProvider>
            </DistanceProvider>
          </FeatureContext.Provider>
        </NotificationsProvider>
      </DialogsProvider>
    </>
  );
};

export default function (props: GetServerSidePropsContext & MainAppProps) {
  const setApolloMutationCompletedAt = useSetAtom(
    apolloMutationCompletedAtAtom,
  );

  const apolloClient = initApollo({
    onMutationComplete: () => {
      setApolloMutationCompletedAt(Date.now());
    },
  });

  const router = useRouter();
  const pathname = router.pathname || (isBrowser && location.pathname);

  const renderPage = () => {
    switch (pathname) {
      case '/':
      case '/signin':
        return <SignIn />;
      case '/forgot-password':
        return <ForgotPassword />;
      case '/set-password':
        return <SetPassword />;
      default:
        return <MainApp {...props} />;
    }
  };

  return (
    <ThemeProvider theme={appTheme}>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Seaber.io" />
        <title>Seaber.io</title>
      </Head>
      <WebSocketStatusNotifier />
      <DatadogInit />
      <GlobalStyle />
      <VersionCheck />
      <GrooveChatWidget />
      <ApolloProvider client={apolloClient}>{renderPage()}</ApolloProvider>
    </ThemeProvider>
  );
}
